import React, { useState, useEffect } from "react";
import { Grid, CircularProgress } from "@mui/material";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import DialogForm from "@/components/modal/DialogForm";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { StyledButton } from "@/components";
import FormikNumberInput from "@/components/forms/FormikNumberInput";
import mapOptions from "@/utils/mappers/mapOptions";
import FormikMonthYearPicker from "@/components/forms/FormikMonthYearPicker";
import dayjs from "dayjs";
import { attendanceMusterListApi, useAddLOPMutation, useGetLOPQuery } from "@/api/services/attendanceManagement/muster/Muster";
import { toast } from "@/components/toastify";
import { useDispatch } from "react-redux";

const AddLOP = ({ open, setOpen, fetchActiveEmployee }) => {

  const [addLOP] = useAddLOPMutation();

  const [selectedDate, setSelectedDate] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [disableReversal, setDisableReversal] = useState(true);



  const validationSchema = Yup.object({
    SelectEmployee: Yup.string().required("Please select an employee"),
    SelectMonth: Yup.date().required("Please select a month and year"),
    LOPDays: Yup.number()
      .typeError("LOP Days must be a number")
      .required("Please enter LOP Days")
      .min(0, "Cannot be negative"),
    LOPReversalDays: Yup.number()
      .typeError("LOP Reversal Days must be a number")
      .required("Please enter LOP Reversal Days")
      .min(0, "Cannot be negative"),
  });

  // Query to fetch LOP data
  const { data: lopData, isFetching } = useGetLOPQuery(
    {
      empId: selectedEmployee,
      month: selectedDate,
    },
    {
      skip: !selectedEmployee || !selectedDate,
    }
  );



  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const data = {
        empId: values.SelectEmployee,
        previousMonth: selectedDate,
        oldLop: values.LOPDays,
        lopReversal: values.LOPReversalDays,
      };
      await addLOP({ data }).unwrap();
      toast("success", "LOP added successfully");
      resetForm();
      setOpen(false);
    } catch (error) {
      console.error("Error adding LOP:", error);
      toast.error(error?.data?.message || "Failed to add LOP");
    } finally {
      setSubmitting(false);
    }
  };

  const dispatch = useDispatch();

  const handleCancel = (setFieldValue, resetForm) => {
    if (selectedEmployee && selectedDate) {
      dispatch(
        attendanceMusterListApi.util.invalidateTags([
          { type: "AttendanceMuster", id: `LOP-${selectedEmployee}-${selectedDate}` },
        ])
      );
    }

    // reset your form state
    setSelectedEmployee("");
    setSelectedDate("");
    setOpen(false);
    setFieldValue("LOPDays", "");
    resetForm({
      values: {
        SelectEmployee: "",
        SelectMonth: "",
        LOPDays: "",
        LOPReversalDays: "",
      },
    });
    setDisableReversal(true);
  };



  return (
    <DialogForm setOpen={setOpen} width={"sm"} height="auto" title={"Add LOP"} open={open}>
      <Formik
        initialValues={{
          SelectEmployee: "",
          SelectMonth: "",
          LOPDays: lopData?.data[0]?.lopCount || "",
          LOPReversalDays: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values, resetForm, isSubmitting }) => {

          useEffect(() => {
            if (lopData?.data?.length > 0) {
              const lopCount = Number(lopData.data[0].lopCount || 0);
              setDisableReversal(lopCount <= 0); // disable reversal if lopCount <= 0
              setFieldValue("LOPDays", lopCount); // update Formik field with lopCount
            } else {
              setDisableReversal(true);
              setFieldValue("LOPDays", "");
            }
          }, [lopData, setFieldValue]);
          return (

            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormikSelectInput
                    name="SelectEmployee"
                    label="Select Employee"
                    required
                    placeholder="Select Employee"
                    search={true}
                    options={mapOptions(fetchActiveEmployee || [])}
                    onChange={(value) => {
                      setFieldValue("SelectEmployee", value.target.value);
                      setSelectedEmployee(value.target.value);
                    }}
                    rows={1.5}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormikMonthYearPicker
                    name="SelectMonth"
                    label="Select Month & Year"
                    pickerType="month"
                    placeholder="Select Month & Year"
                    required
                    maxDate={new Date()}
                    shouldDisableMonth={(date) => {
                      const now = new Date();
                      return (
                        date.getMonth() === now.getMonth() &&
                        date.getFullYear() === now.getFullYear()
                      );
                    }}
                    value={values.SelectMonth ? dayjs(values.SelectMonth, "MM-YYYY").format("MMMM YYYY") : ""}
                    onChange={(date) => {
                      const formattedDate = dayjs(date).format("MM-YYYY");
                      setFieldValue("SelectMonth", formattedDate);
                      setSelectedDate(formattedDate);
                      if (selectedEmployee && formattedDate) {
                        setSelectedDate(formattedDate);
                      }
                    }}

                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormikNumberInput
                    name="LOPDays"
                    label="LOP Days"
                    required
                    placeholder="Enter here"
                    disabled={true}

                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormikNumberInput
                    name="LOPReversalDays"
                    label="LOP Reversal Days"
                    placeholder="Enter here"
                    required
                    disabled={disableReversal}
                  />
                </Grid>
              </Grid>
              <Grid container justifyContent="flex-end" spacing={2} mt={1}>
                <Grid item xs={12} sm={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <StyledButton
                    label={"Cancel"}
                    type="button"
                    variant="outlined"
                    onClick={() => handleCancel(setFieldValue, resetForm)}
                    sx={{ mr: 1 }}
                  />
                  <StyledButton
                    label={isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Submit"}
                    type="submit"
                    variant="contained"
                  />
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </DialogForm>
  );
};

export default AddLOP;
